<template>
  <section class="operational-insight-view">
    <LastWorkingWeekView
      v-if="hasWeeklyReport"
      :sections="weeklyReport.sections"
      :ticket-refs="ticketRefs"
      @ticket-ref-clicked="$emit('ticket-ref-clicked', $event)"
    />

    <YesterdayReportView
      v-else-if="hasYesterdayReport"
      :sections="yesterdayReport.sections"
      :ticket-refs="ticketRefs"
      @ticket-ref-clicked="$emit('ticket-ref-clicked', $event)"
    />

    <AskReportView
      v-else
      :report-view="reportView"
      :signal-strength="signalStrength"
      :hide-data-context="true"
      :ticket-refs="ticketRefs"
      @ticket-ref-clicked="$emit('ticket-ref-clicked', $event)"
    />

    <section v-if="!hasYesterdayReport && !hasWeeklyReport && (dataContextItems.length || limitations.length || demoNote)" class="operational-data-context mode-card">
      <p class="data-context-label">Data context and limits</p>
      <dl v-if="dataContextItems.length" class="dataset-context">
        <div v-for="item in dataContextItems" :key="item.label" class="dataset-context__item">
          <dt>{{ item.label }}</dt>
          <dd>{{ item.value }}</dd>
        </div>
      </dl>
      <div v-if="limitations.length" class="data-limits">
        <p class="data-limits__subheading">Limits</p>
        <ul class="data-limits__list">
          <li v-for="(limitation, idx) in limitations" :key="`op-limitation-${idx}`">{{ limitation }}</li>
        </ul>
      </div>
      <div v-if="demoNote" class="data-limits">
        <p class="data-limits__subheading">Demo data</p>
        <p class="data-limits__note">{{ demoNote }}</p>
      </div>
    </section>
  </section>
</template>

<script>
import AskReportView from '@/components/roster/ask/AskReportView.vue'
import YesterdayReportView from '@/components/roster/ask/YesterdayReportView.vue'
import LastWorkingWeekView from '@/components/roster/ask/LastWorkingWeekView.vue'

export default {
  name: 'OperationalInsightView',
  components: {
    AskReportView,
    YesterdayReportView,
    LastWorkingWeekView,
  },
  props: {
    hasYesterdayReport: {
      type: Boolean,
      default: false,
    },
    yesterdayReport: {
      type: Object,
      default: () => ({
        subtitle: '',
        sections: [],
      }),
    },
    hasWeeklyReport: {
      type: Boolean,
      default: false,
    },
    weeklyReport: {
      type: Object,
      default: () => ({
        subtitle: '',
        sections: [],
      }),
    },
    signalStrength: {
      type: Object,
      default: () => ({
        strength: '',
        mode: '',
        label: 'Overview',
        chipLabel: '',
        isHigh: false,
        isLow: false,
      }),
    },
    reportView: {
      type: Object,
      default: () => null,
    },
    ticketRefs: {
      type: Array,
      default: () => [],
    },
  },
  emits: ['ticket-ref-clicked'],
  computed: {
    dataContextItems() {
      return Array.isArray(this.reportView?.dataContextAndLimits?.items)
        ? this.reportView.dataContextAndLimits.items
            .map((item) => ({ label: String(item?.label || '').trim(), value: String(item?.value || '').trim() }))
            .filter((item) => item.label && item.value)
        : []
    },
    limitations() {
      return Array.isArray(this.reportView?.dataContextAndLimits?.limitations)
        ? this.reportView.dataContextAndLimits.limitations
        : []
    },
    demoNote() {
      const note = this.reportView?.dataContextAndLimits?.demoNote
      return typeof note === 'string' ? note.trim() : ''
    },
  },
}
</script>

<style lang="scss" scoped>
.operational-insight-view {
  display: flex;
  flex-direction: column;
  gap: $space-4;
}

.operational-data-context {
  border: 1px solid $color-border;
  border-radius: $radius-md;
  background: $color-surface;
  padding: $space-3;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.03);
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.data-context-label {
  margin: 0 0 $space-1;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.dataset-context {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: $space-3;
  margin: 0;
}

.dataset-context__item {
  display: flex;
  flex-direction: column;
  gap: $space-1;
}

.dataset-context__item dt {
  margin: 0;
  font-size: $font-size-xs;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: $color-text-strong;
}

.dataset-context__item dd {
  margin: 0;
}

.data-limits {
  display: flex;
  flex-direction: column;
  gap: $space-2;
}

.data-limits__subheading {
  margin: 0;
  font-size: $font-size-sm;
  font-weight: 600;
  color: $color-text-strong;
}

.data-limits__list {
  margin: 0;
  padding-left: 1.2rem;
  list-style-type: disc;
}

.data-limits__list li + li {
  margin-top: $space-2;
}

.data-limits__note {
  margin: 0;
}

@media (max-width: 900px) {
  .dataset-context {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 700px) {
  .operational-insight-view {
    gap: $space-3;
  }
}
</style>
